<template>
  <div
    class="flex items-center justify-center h-12 gap-4 px-4 py-3 text-xs text-white banner dark:text-white"
    :class="bannerClasses"
  >
    <span class="banner-message">
      {{ bannerMessage }}
      <a
        v-if="hrefLink"
        :href="hrefLink"
        rel="noopener noreferrer nofollow"
        target="_blank"
      >
        {{ hrefLinkText }}
      </a>
    </span>
    <div class="actions">
      <otello-button
        v-if="hasActionButton"
        size="tiny"
        :icon="actionButtonIcon"
        :is-disabled="isActionButtonLoading"
        :is-loading="isActionButtonLoading"
        :variant="actionButtonVariant"
        color-scheme="primary"
        class-names="banner-action__button"
        @click="onClick"
      >
        {{ actionButtonLabel }}
      </otello-button>
      <otello-button
        v-if="hasCloseButton"
        size="tiny"
        :color-scheme="colorScheme"
        :icon="closeButtonIcon"
        class-names="banner-action__button"
        @click="onClickClose"
      >
        {{ $t('GENERAL_SETTINGS.DISMISS') }}
      </otello-button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    bannerMessage: {
      type: String,
      default: '',
    },
    hrefLink: {
      type: String,
      default: '',
    },
    hrefLinkText: {
      type: String,
      default: '',
    },
    hasActionButton: {
      type: Boolean,
      default: false,
    },
    actionButtonVariant: {
      type: String,
      default: '',
    },
    actionButtonLabel: {
      type: String,
      default: '',
    },
    actionButtonIcon: {
      type: String,
      default: 'arrow-right',
    },
    isActionButtonLoading: {
      type: Boolean,
      default: false,
    },
    colorScheme: {
      type: String,
      default: '',
    },
    hasCloseButton: {
      type: Boolean,
      default: false,
    },
    closeButtonIcon: {
      type: String,
      default: 'dismiss-circle',
    },
  },
  computed: {
    bannerClasses() {
      const classList = [this.colorScheme];

      if (this.hasActionButton || this.hasCloseButton) {
        classList.push('has-button');
      }
      return classList;
    },
  },
  methods: {
    onClick(e) {
      this.$emit('click', e);
    },
    onClickClose(e) {
      this.$emit('close', e);
    },
  },
};
</script>

<style lang="scss" scoped>
.banner {
  &.primary {
    @apply bg-otello-500 dark:bg-otello-500;
    .banner-action__button {
      @apply bg-otello-600 dark:bg-otello-600 border-none text-white;

      &:hover {
        @apply bg-otello-700 dark:bg-otello-700;
      }
    }
  }

  &.info {
    background-color: #0178f9;
    .banner-action__button {
      background-color: #0165d4;
      @apply border-none text-white;

      &:hover {
        background-color: #0155b5;
      }
    }
  }

  &.secondary {
    @apply bg-slate-200 dark:bg-slate-300 text-slate-800 dark:text-slate-800;
    a {
      @apply text-slate-800 dark:text-slate-800;
    }
  }

  &.alert {
    @apply bg-red-500 dark:bg-red-500;
    .banner-action__button {
      @apply bg-red-700 dark:bg-red-700 border-none text-white dark:text-white;

      &:hover {
        @apply bg-red-800 dark:bg-red-800;
      }
    }
  }

  &.warning {
    @apply bg-yellow-500 dark:bg-yellow-600 text-slate-900 dark:text-slate-900;
    a {
      @apply text-slate-900 dark:text-slate-900 font-medium;
    }
    .banner-action__button {
      @apply bg-slate-700 border-none text-white font-medium px-4 py-1.5 rounded-md;

      &:hover {
        @apply bg-slate-800;
      }
    }
  }

  &.gray {
    @apply text-black-500 dark:text-black-500;
    .banner-action__button {
      @apply text-white dark:text-white;
    }
  }

  a {
    @apply ml-1 underline text-white dark:text-white text-xs;
  }

  /* Ensure readable link color on neutral banners */
  &.secondary a {
    @apply text-slate-800 dark:text-slate-800;
  }

  .banner-action__button {
    :deep(.button__content) {
      @apply whitespace-nowrap;
    }
  }

  .banner-message {
    @apply flex items-center;
  }

  .actions {
    @apply flex gap-1 right-3;
  }
}
</style>
